import { useState, useEffect, useContext, useMemo } from 'react';
import { isEffectivelyComplete, leafTasks } from '@harvus/shared';
import { B, font, radius, weight } from '../constants/brand';
import { fmt } from '../utils/format';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { UserContext } from '../context/UserContext';
import { Btn, Card, Spinner, ErrorMsg, useToast, PageHeader, Icon, clickable } from '../components/ui';
import { OverdueReportModal } from '../components/tasks/OverdueReportModal';
import { TaskDetailModal } from '../components/shared/TaskDetailModal';
import { EDITABLE_STATUSES, STATUS_COLORS, GroupHeader, TaskRow, KanbanCard, QuickNoteComposer, completeRefreshDelay } from '../components/tasks/TaskComponents';
import { TaskCreateModal } from '../components/tasks/TaskCreateModal';
import { useTaskCreate, genRequestId } from '../components/tasks/useTaskCreate';

export const Tasks = ({ targetTaskId, targetNoteId, onTargetHandled }) => {
  const { toast } = useToast();
  const currentUser = useContext(UserContext);
  const [showOverdueReport, setShowOverdueReport] = useState(false);
  const { data: tasks, loading, error, reload } = useApi(() => api.get('tasks'), []);
  const { data: users } = useApi(() => api.get('users'), []);
  const { data: deals } = useApi(() => api.get('deals'), []);
  const { data: contacts } = useApi(() => api.get('contacts'), []);
  const [view, setView]           = useState('list');
  const [groupBy, setGroupBy]     = useState('deal');
  const [collapsed, setCollapsed] = useState({});
  const [expanded, setExpanded]   = useState({});
  const [selected, setSelected]   = useState(null);
  // A note id to scroll to / highlight once the task detail's notes feed loads
  // (captured locally because App clears targetNoteId as soon as we navigate).
  const [pendingNoteId, setPendingNoteId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [dragOver, setDragOver]   = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [search, setSearch] = useState('');
  const [quickNoteFor, setQuickNoteFor]   = useState(null);  // taskID with an open quick-note composer

  // Creating a task — the post, the idempotency key and the near-duplicate gate
  // — lives in one place for every surface that creates one (see useTaskCreate).
  // This instance serves QUICK-ADD; the New Task dialog owns its own.
  const { runTaskCreate, duplicateDialog } = useTaskCreate();




  const [quickAdd, setQuickAdd]   = useState(false);
  const [quickTitle, setQuickTitle] = useState('');
  const [addingChildFor, setAddingChildFor] = useState(null);

  const openQuickNote = (task) => setQuickNoteFor(task.taskID);

  const changeStatus = async (task, status) => {
    try {
      await api.put(`tasks/${task.taskID}/status`, { status });
      await completeRefreshDelay(status);
      reload();
      return true;
    } catch (e) { toast.error(e.message); return false; }
  };

  const openDetail = t => setSelected(t);

  // Open the targeted task once it's loaded — supports cross-page links from the Activity Report and Task Report.
  useEffect(() => {
    if (!targetTaskId || !tasks) return;
    const t = tasks.find(t => t.taskID === targetTaskId);
    if (t) { setPendingNoteId(targetNoteId || null); openDetail(t); onTargetHandled?.(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetTaskId, tasks]);




  const quickAddTask = async (andClose = false) => {
    if (!quickTitle.trim()) { if (andClose) { setQuickAdd(false); } return; }
    // Per-submit key — dedupes the low-level auto-retry after a dropped
    // connection (the retry reuses this same body) so one quick-add can't
    // land twice.
    try {
      await runTaskCreate({ title: quickTitle.trim(), priority: 'Medium', status: 'Not Started', clientRequestId: genRequestId() }, () => {
        setQuickTitle(''); reload();
        if (andClose) setQuickAdd(false);
      });
    } catch (e) { toast.error(e.message); }
  };

  const openAddChild = (parentTask) => {
    setAddingChildFor(parentTask);
    setShowModal(true);
  };

  const handleDragStart = (e, task) => { e.dataTransfer.setData('taskID', task.taskID); };
  const handleDrop = async (e, status) => {
    e.preventDefault();
    const taskID = e.dataTransfer.getData('taskID');
    if (!taskID) return;
    setDragOver(null);
    try { await api.put(`tasks/${taskID}/status`, { status }); reload(); } catch {}
  };

  const toggleGroup  = key => setCollapsed(p => ({ ...p, [key]: !p[key] }));
  const toggleExpand = id  => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const buildDealGroups = (allTasks) => {
    // Look up a deal's Harvus lead(s) from the (already-loaded) deals list, so
    // the group-header badge is present on first paint.
    const leadByDeal = new Map((deals || []).map(d => [d.dealID, d.harvusLeadNames || null]));
    const parents = allTasks.filter(t => !t.parentTaskID);
    const byDeal = {};
    for (const t of parents) {
      const key   = t.dealID  || '__none__';
      const label = t.dealTitle || 'No Deal';
      if (!byDeal[key]) {
        // Show the deal's Harvus lead(s) next to the deal in the group header.
        const lead = key === '__none__' ? null : (leadByDeal.get(key) || null);
        byDeal[key] = { key, label, lead, tasks: [] };
      }
      byDeal[key].tasks.push(t);
    }

    // Within each deal, build sub-groups by DealDetail when the deal's tasks span
    // 2+ distinct details (a single detail-with-tasks doesn't need a sub-break).
    // The detail label comes off the task's own DealDetailTitle (returned by the
    // API), so the grouping is complete on first paint — no dependency on the
    // deal-picker detail fetch, which is what used to make the list re-layout a
    // few ms after load. Deal-level tasks (no DealDetailID) render flat above the
    // sub-breaks — no header.
    for (const dealKey of Object.keys(byDeal)) {
      if (dealKey === '__none__') continue;
      const group = byDeal[dealKey];
      const distinctDetails = new Set(group.tasks.map(t => t.dealDetailID).filter(Boolean));
      if (distinctDetails.size < 2) continue;

      const bySub = {};
      const looseTasks = [];
      for (const t of group.tasks) {
        if (!t.dealDetailID) { looseTasks.push(t); continue; }
        const subKey = `${dealKey}::detail::${t.dealDetailID}`;
        const subLabel = t.dealDetailTitle || 'Detail';
        if (!bySub[subKey]) bySub[subKey] = { key: subKey, label: subLabel, tasks: [] };
        bySub[subKey].tasks.push(t);
      }
      group.looseTasks = looseTasks;
      group.subGroups  = Object.values(bySub).sort((a, b) => a.label.localeCompare(b.label));
    }

    const groups = [];
    if (byDeal['__none__']) groups.push(byDeal['__none__']);
    for (const [k, g] of Object.entries(byDeal)) { if (k !== '__none__') groups.push(g); }
    return groups;
  };

  const buildAssigneeGroups = (allTasks) => {
    const byAssignee = {};
    for (const t of allTasks) {
      const names = (t.assigneeNames || '').split(', ').filter(Boolean);
      if (names.length === 0) {
        if (!byAssignee['__none__']) byAssignee['__none__'] = { key: '__none__', label: 'Unassigned', tasks: [] };
        byAssignee['__none__'].tasks.push(t);
      } else {
        for (const name of names) {
          if (!byAssignee[name]) byAssignee[name] = { key: name, label: name, tasks: [] };
          byAssignee[name].tasks.push(t);
        }
      }
    }
    // Sort named assignees: the current user first, then alphabetically by name.
    const myName = currentUser?.displayName || currentUser?.name;
    const named = Object.entries(byAssignee)
      .filter(([k]) => k !== '__none__')
      .map(([, g]) => g)
      .sort((a, b) => {
        if (myName && a.label === myName) return -1;
        if (myName && b.label === myName) return 1;
        return a.label.localeCompare(b.label);
      });

    const groups = [...named];
    if (byAssignee['__none__']) groups.push(byAssignee['__none__']);
    return groups;
  };

  const buildPriorityGroups = (allTasks) => {
    const order = ['Critical', 'High', 'Medium', 'Low'];
    const byPri = {};
    for (const t of allTasks) {
      const p = t.priority || 'Low';
      if (!byPri[p]) byPri[p] = { key: p, label: p, tasks: [] };
      byPri[p].tasks.push(t);
    }
    return order.filter(p => byPri[p]).map(p => byPri[p]);
  };

  // Free-text search across a task's title, deal, assignees, outside parties,
  // priority and status. Matching is case-insensitive substring on the joined
  // fields (separated so a query can't span two fields).
  const q = search.trim().toLowerCase();
  const matchesSearch = (t) => {
    if (!q) return true;
    return [t.title, t.dealTitle, t.assigneeNames, t.outsidePartyNames, t.priority, t.status]
      .filter(Boolean).join('  ').toLowerCase().includes(q);
  };

  // The set of task IDs kept when a search is active: every directly-matching
  // task plus its ancestors (so a matched subtask stays reachable under its
  // parent) and its descendants (so a matched parent shows its full subtree).
  const searchMatchIds = useMemo(() => {
    const ids = new Set();
    if (!q || !tasks) return ids;
    const byId = new Map(tasks.map(t => [t.taskID, t]));
    const childrenMap = new Map();
    for (const t of tasks) {
      if (!t.parentTaskID) continue;
      const arr = childrenMap.get(t.parentTaskID) || [];
      arr.push(t);
      childrenMap.set(t.parentTaskID, arr);
    }
    for (const t of tasks) {
      if (!matchesSearch(t)) continue;
      ids.add(t.taskID);
      let p = t.parentTaskID;
      while (p && byId.has(p) && !ids.has(p)) { ids.add(p); p = byId.get(p).parentTaskID; }
      const stack = [...(childrenMap.get(t.taskID) || [])];
      while (stack.length) {
        const c = stack.pop();
        if (ids.has(c.taskID)) continue;
        ids.add(c.taskID);
        stack.push(...(childrenMap.get(c.taskID) || []));
      }
    }
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, tasks]);
  // Pre-index children by parent once per data change (O(n)), so per-row lookups
  // during render are O(1) instead of scanning + sorting the whole task array for
  // every rendered row (which made the list O(n²) and janky on large task sets).
  const childrenByParent = useMemo(() => {
    const map = new Map();
    for (const t of (tasks || [])) {
      if (!t.parentTaskID) continue;
      if (!(showCompleted || !isEffectivelyComplete(t))) continue;
      if (!(!q || searchMatchIds.has(t.taskID))) continue;
      const arr = map.get(t.parentTaskID) || [];
      arr.push(t);
      map.set(t.parentTaskID, arr);
    }
    const byDue = (a, b) => {
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return new Date(a.dueDate) - new Date(b.dueDate);
    };
    for (const arr of map.values()) arr.sort(byDue);
    return map;
  }, [tasks, showCompleted, q, searchMatchIds]);
  const childrenOf = (taskID) => childrenByParent.get(taskID) || [];

  // Visible (non-completed unless toggled, search-matched) task set + the grouped
  // view, memoized so typing in search / toggling rows doesn't rebuild the whole
  // group tree on every unrelated re-render.
  // "Completed" is the EFFECTIVE completion (shared/src/taskTree.ts): a group
  // has no state of its own and is done when its subtasks are, so a heading
  // whose subtasks are all finished leaves the open list with them.
  const visibleTasks = useMemo(() => {
    const all = tasks || [];
    return (showCompleted ? all : all.filter(t => !isEffectivelyComplete(t)))
      .filter(t => !q || searchMatchIds.has(t.taskID));
  }, [tasks, showCompleted, q, searchMatchIds]);
  // The flat views hold the WORK: a group is a heading, not a card or a bar.
  const visibleLeafTasks = useMemo(() => leafTasks(visibleTasks), [visibleTasks]);

  const groups = useMemo(() => (
    groupBy === 'deal'     ? buildDealGroups(visibleTasks)
    : groupBy === 'assignee' ? buildAssigneeGroups(visibleTasks)
    : buildPriorityGroups(visibleTasks)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [groupBy, visibleTasks, deals, currentUser]);

  // Expandable = has children that survive the current filters (show-completed /
  // search), not the backend's raw ChildCount — otherwise a parent whose subtasks
  // are all completed (and hidden) would be treated as expandable and the "Show
  // All Subtasks" toggle would expand it to nothing.
  const expandableTaskIds = useMemo(
    () => Array.from(childrenByParent.keys()),
    [childrenByParent],
  );

  const renderTaskRows = (taskList, depth = 0) => taskList.map(task => {
    const children   = childrenOf(task.taskID);
    // While searching, auto-expand any row that still has (matching) children
    // so matched subtasks are visible without manually drilling in.
    const isExpanded = expanded[task.taskID] || (!!q && children.length > 0);
    return (
      <div key={task.taskID}>
        {/* content-visibility lets the browser skip layout/paint for rows the
            user hasn't scrolled to yet (the main cost on long task lists), while
            keeping every row in the DOM. Wrap the row only — not its expanded
            subtree — so the reserved-size estimate stays per-row. */}
        <div style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 44px' }}>
          <TaskRow task={task} depth={depth} expanded={isExpanded} onToggle={toggleExpand}
            onOpen={openDetail} onAddChild={depth === 0 ? openAddChild : null}
            onStatusChange={changeStatus} onQuickNote={openQuickNote} visibleChildCount={children.length}
            fadeOnComplete={!showCompleted} />
        </div>
        {quickNoteFor === task.taskID && (
          <QuickNoteComposer taskId={task.taskID} taskTitle={task.title} onClose={() => setQuickNoteFor(null)} />
        )}
        {isExpanded && children.length > 0 && renderTaskRows(children, depth + 1)}
      </div>
    );
  });

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} onRetry={reload} />;

  const allTasks     = tasks || [];

  // "Show All Subtasks" — expand every task that has children (all nesting
  // levels), so the full subtask tree is visible without clicking each chevron.
  const allSubtasksExpanded = expandableTaskIds.length > 0 && expandableTaskIds.every(id => expanded[id]);
  const toggleAllSubtasks  = () =>
    setExpanded(allSubtasksExpanded ? {} : Object.fromEntries(expandableTaskIds.map(id => [id, true])));

  const ListHeader = () => (
    <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: `2px solid ${B.border}`, background: B.surfaceMuted, fontSize: 11, fontWeight: 600, color: B.textMd, letterSpacing: 0.5, textTransform: 'uppercase' }}>
      <div style={{ width: 20 }} />
      <div style={{ width: 16, marginRight: 10 }} />
      <div style={{ flex: 1 }}>Task</div>
      <div style={{ width: 100, textAlign: 'center' }}>Status</div>
      <div style={{ width: 80,  textAlign: 'center' }}>Priority</div>
      <div style={{ width: 90,  textAlign: 'right'  }}>Due</div>
      <div style={{ width: 80,  textAlign: 'right', paddingLeft: 8 }}>Assignees</div>
    </div>
  );


  return (
    <div>
      <PageHeader
        icon="tasks"
        title="Tasks"
        actions={
          <>
            {currentUser?.role === 'Admin' && (
              <Btn variant="secondary" onClick={() => setShowOverdueReport(true)} title="Email yourself one person's overdue tasks, to forward to them">Email overdue tasks</Btn>
            )}
            <Btn variant="secondary" onClick={() => { setQuickAdd(v => !v); setQuickTitle(''); }}><Icon name="quickAdd" size={15} /> Quick Add</Btn>
            <Btn onClick={() => { setAddingChildFor(null); setShowModal(true); }}><Icon name="add" size={15} /> New Task</Btn>
          </>
        }
      />

      {quickAdd && (
        <div style={{ background: B.tealLt, border: `1px solid ${B.tealDk}`, borderRadius: radius.xl2, padding: '12px 14px', marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: B.navyDk, whiteSpace: 'nowrap' }}>Quick Add:</span>
          <input autoFocus value={quickTitle} onChange={e => setQuickTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); quickAddTask(false); } if (e.key === 'Escape') { setQuickAdd(false); setQuickTitle(''); } }}
            placeholder="Type task title and press Enter to save, Esc to close…"
            style={{ flex: 1, border: `1px solid ${B.border}`, borderRadius: radius.lg, padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', color: B.text, background: B.surface, outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s' }}
            onFocus={e => { e.currentTarget.style.borderColor = B.accent; e.currentTarget.style.boxShadow = B.ringAccent; }}
            onBlur={e => { e.currentTarget.style.borderColor = B.border; e.currentTarget.style.boxShadow = 'none'; }} />
          <Btn size="sm" onClick={() => quickAddTask(false)} disabled={!quickTitle.trim()}>Add</Btn>
          <Btn size="sm" variant="ghost" onClick={() => { setQuickAdd(false); setQuickTitle(''); }}>Done</Btn>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 200, maxWidth: 380 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', display: 'inline-flex' }}>
            <Icon name="search" size={15} color={B.textLt} />
          </span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape') setSearch(''); }}
            placeholder="Search tasks, deals, people…"
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 30px 8px 34px', fontSize: 13,
              border: `1px solid ${B.border}`, borderRadius: radius.pill, fontFamily: 'inherit', color: B.text, background: B.surface, outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s' }}
            onFocus={e => { e.currentTarget.style.borderColor = B.accent; e.currentTarget.style.boxShadow = B.ringAccent; }}
            onBlur={e => { e.currentTarget.style.borderColor = B.border; e.currentTarget.style.boxShadow = 'none'; }} />
          {search && (
            <button onClick={() => setSearch('')} aria-label="Clear search"
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: B.textLt, display: 'inline-flex', padding: 2 }}>
              <Icon name="close" size={13} />
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 3, background: B.surfaceMuted, borderRadius: radius.pill, padding: 4, border: `1px solid ${B.divider}` }}>
          {[['list','list','List'], ['kanban','kanban','Kanban'], ['gantt','gantt','Gantt']].map(([v, ic, lbl]) => {
            const on = view === v;
            return (
              <button key={v} onClick={() => setView(v)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: radius.pill, border: 'none', cursor: 'pointer', background: on ? B.surface : 'transparent', color: on ? B.navyDk : B.textMd, boxShadow: on ? B.shadowXs : 'none', fontSize: font.sm, fontWeight: on ? weight.semibold : weight.medium, fontFamily: 'inherit', transition: 'background 0.15s, color 0.15s, box-shadow 0.15s' }}
                onMouseEnter={e => { if (!on) e.currentTarget.style.color = B.text; }}
                onMouseLeave={e => { if (!on) e.currentTarget.style.color = B.textMd; }}><Icon name={ic} size={14} /> {lbl}</button>
            );
          })}
        </div>
        {view === 'list' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: B.textMd, fontWeight: 600 }}>Group by:</span>
            {[['deal','Deal'], ['assignee','Assignee'], ['priority','Priority']].map(([v, lbl]) => (
              <button key={v} onClick={() => setGroupBy(v)} style={{ padding: '5px 14px', borderRadius: 20, border: `1px solid ${groupBy === v ? B.navy : B.border}`, background: groupBy === v ? B.navy : B.white, color: groupBy === v ? '#fff' : B.textMd, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>{lbl}</button>
            ))}
          </div>
        )}
        {view === 'list' && expandableTaskIds.length > 0 && (
          <button onClick={toggleAllSubtasks}
            style={{ padding: '5px 14px', borderRadius: 20, border: `1px solid ${allSubtasksExpanded ? B.tealDk : B.border}`, background: allSubtasksExpanded ? B.tealLt : B.white, color: allSubtasksExpanded ? B.tealDk : B.textMd, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name={allSubtasksExpanded ? 'chevronDown' : 'chevronRight'} size={13} /> {allSubtasksExpanded ? 'Collapse All Subtasks' : 'Show All Subtasks'}</span>
          </button>
        )}
        <button onClick={() => setShowCompleted(v => !v)}
          style={{ padding: '5px 14px', borderRadius: 20, border: `1px solid ${showCompleted ? B.tealDk : B.border}`, background: showCompleted ? B.tealLt : B.white, color: showCompleted ? B.tealDk : B.textMd, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>
          {showCompleted ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="check" size={13} /> Showing Completed</span> : 'Show Completed'}
        </button>
      </div>

      {view === 'list' && (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <ListHeader />
          {groups.map(group => (
            <div key={group.key}>
              <GroupHeader label={group.label} lead={group.lead} count={group.tasks.length} collapsed={collapsed[group.key]} onToggle={() => toggleGroup(group.key)} />
              {!collapsed[group.key] && (
                group.subGroups ? (
                  <>
                    {group.looseTasks?.length > 0 && renderTaskRows(group.looseTasks)}
                    {group.subGroups.map(sub => (
                      <div key={sub.key}>
                        <div {...clickable(() => toggleGroup(sub.key))} style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '6px 12px 6px 28px',
                          background: B.white,
                          cursor: 'pointer', userSelect: 'none',
                          borderBottom: `1px solid ${B.border}`,
                          borderLeft: `3px solid ${B.tealDk}`,
                          marginLeft: 16,
                        }}>
                          <span style={{ color: B.textMd, transition: 'transform 0.15s', display: 'inline-flex', transform: collapsed[sub.key] ? 'rotate(-90deg)' : 'rotate(0deg)' }}><Icon name="chevronDown" size={12} /></span>
                          <span style={{ fontWeight: 600, fontSize: 11, color: B.textMd, flex: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>{sub.label}</span>
                          <span style={{ fontSize: 10, color: B.textLt, background: B.surfaceMuted, borderRadius: 10, padding: '1px 7px', border: `1px solid ${B.border}` }}>{sub.tasks.length}</span>
                        </div>
                        {!collapsed[sub.key] && renderTaskRows(sub.tasks)}
                      </div>
                    ))}
                  </>
                ) : renderTaskRows(group.tasks)
              )}
            </div>
          ))}
          {visibleTasks.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: B.textLt, fontSize: 13 }}>{q ? `No tasks match "${search.trim()}".` : allTasks.length > 0 ? 'All tasks completed. Click "Show Completed" to view them.' : 'No tasks found.'}</div>}
        </Card>
      )}

      {view === 'kanban' && (
        <div className="harvus-responsive-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${EDITABLE_STATUSES.length}, 1fr)`, gap: 16, alignItems: 'start' }}>
          {EDITABLE_STATUSES.map(status => {
            const colTasks = visibleLeafTasks.filter(t => (t.status || 'Not Started') === status);
            const isOver   = dragOver === status;
            return (
              <div key={status} onDragOver={e => { e.preventDefault(); setDragOver(status); }} onDragLeave={() => setDragOver(null)} onDrop={e => handleDrop(e, status)}
                style={{ background: isOver ? B.tealLt : B.surfaceMuted, borderRadius: radius.xxl, padding: 12, border: `2px solid ${isOver ? B.tealDk : 'transparent'}`, minHeight: 200, transition: 'all 0.15s' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_COLORS[status] }} />
                    <span style={{ fontWeight: 700, fontSize: 13, color: B.text }}>{status}</span>
                  </div>
                  <span style={{ fontSize: 11, color: B.textLt, background: B.white, borderRadius: 10, padding: '1px 8px', border: `1px solid ${B.border}` }}>{colTasks.length}</span>
                </div>
                {colTasks.map(task => <KanbanCard key={task.taskID} task={task} onOpen={openDetail} onDragStart={handleDragStart} />)}
                {colTasks.length === 0 && <div style={{ textAlign: 'center', padding: '20px 0', color: B.textLt, fontSize: 12 }}>{isOver ? 'Drop here' : 'No tasks'}</div>}
              </div>
            );
          })}
        </div>
      )}

      {view === 'gantt' && (() => {
        const ganttTasks = [...visibleLeafTasks.filter(t => t.dueDate)].sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
        if (ganttTasks.length === 0) return (
          <Card><div style={{ padding: 40, textAlign: 'center', color: B.textLt }}>No tasks with due dates. Set a due date on a task to see it here.</div></Card>
        );
        const today = new Date(); today.setHours(0,0,0,0);
        const earliest = new Date(ganttTasks[0].dueDate); earliest.setHours(0,0,0,0);
        const latest   = new Date(ganttTasks[ganttTasks.length-1].dueDate); latest.setHours(0,0,0,0);
        const winStart = new Date(Math.min(earliest.getTime() - 14*86400000, today.getTime() - 7*86400000));
        const winEnd   = new Date(latest.getTime() + 14*86400000);
        const totalMs  = winEnd - winStart;
        const pct      = d => ((new Date(d) - winStart) / totalMs) * 100;
        const todayPct = Math.max(0, Math.min(100, pct(today)));
        const weeks = [];
        const wk = new Date(winStart);
        wk.setDate(wk.getDate() + ((1 - wk.getDay() + 7) % 7));
        while (wk <= winEnd) {
          weeks.push({ pct: pct(wk), label: wk.toLocaleDateString('default', { month: 'short', day: 'numeric' }) });
          wk.setDate(wk.getDate() + 7);
        }
        const months = [];
        const mCur = new Date(winStart); mCur.setDate(1);
        while (mCur <= winEnd) {
          const p = pct(mCur);
          if (p >= 0) months.push({ pct: p, label: mCur.toLocaleString('default', { month: 'short', year: '2-digit' }) });
          mCur.setMonth(mCur.getMonth() + 1);
        }
        const PRIORITY_COLORS = { Critical: B.red, High: B.amber, Medium: B.tealDk, Low: B.navy };
        return (
          <Card style={{ padding: 0, overflow: 'auto' }}>
            <div style={{ minWidth: 800 }}>
              <div style={{ display: 'flex', borderBottom: `1px solid ${B.border}`, background: B.surfaceMuted, position: 'sticky', top: 0, zIndex: 5 }}>
                <div style={{ width: 260, flexShrink: 0, padding: '8px 12px', fontSize: 11, fontWeight: 700, color: B.textMd, letterSpacing: 0.5, borderRight: `1px solid ${B.border}` }}>TASK ({ganttTasks.length})</div>
                <div style={{ flex: 1, position: 'relative', height: 32 }}>
                  {weeks.map((w, i) => <div key={i} style={{ position: 'absolute', left: `${w.pct}%`, top: 0, bottom: 0, width: 1, background: B.border }} />)}
                  {months.map((m, i) => <div key={i} style={{ position: 'absolute', left: `${m.pct}%`, top: 8, fontSize: 10, color: B.textMd, fontWeight: 700, whiteSpace: 'nowrap', paddingLeft: 4 }}>{m.label}</div>)}
                  {todayPct >= 0 && todayPct <= 100 && <div style={{ position: 'absolute', left: `${todayPct}%`, top: 2, fontSize: 9, color: B.red, fontWeight: 700, transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>TODAY</div>}
                </div>
              </div>
              {ganttTasks.map(task => {
                const duePct  = Math.max(0, Math.min(100, pct(task.dueDate)));
                const overdue = new Date(task.dueDate) < today && !task.isCompleted;
                const barColor = task.isCompleted ? B.green : (overdue ? B.red : (PRIORITY_COLORS[task.priority] || B.navy));
                return (
                  <div key={task.taskID} style={{ display: 'flex', borderBottom: `1px solid ${B.border}`, alignItems: 'center', background: '#fff' }}
                    onMouseEnter={e => e.currentTarget.style.background = B.surfaceMuted} onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                    <div {...clickable(() => openDetail(task))} aria-label={`Open task: ${task.title}`} style={{ width: 260, flexShrink: 0, padding: '7px 12px', borderRight: `1px solid ${B.border}`, cursor: 'pointer', overflow: 'hidden' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {task.isCompleted && <span style={{ color: B.green, display: 'inline-flex' }}><Icon name="check" size={12} /></span>}
                        {overdue && !task.isCompleted && <span style={{ color: B.red, display: 'inline-flex' }}><Icon name="warning" size={12} /></span>}
                        <span style={{ fontSize: 12, fontWeight: 600, color: task.isCompleted ? B.textLt : B.text, textDecoration: task.isCompleted ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.title}</span>
                      </div>
                      <div style={{ fontSize: 10, color: B.textLt, marginTop: 1 }}>{task.dealTitle || 'No deal'} · {fmt.date(task.dueDate)}</div>
                    </div>
                    <div style={{ flex: 1, position: 'relative', height: 40 }}>
                      {weeks.map((w, i) => <div key={i} style={{ position: 'absolute', left: `${w.pct}%`, top: 0, bottom: 0, width: 1, background: B.border, opacity: 0.5 }} />)}
                      {todayPct >= 0 && todayPct <= 100 && <div style={{ position: 'absolute', left: `${todayPct}%`, top: 0, bottom: 0, width: 2, background: B.red, opacity: 0.7, zIndex: 2 }} />}
                      <div {...clickable(() => openDetail(task))} title={`${task.title} — due ${fmt.date(task.dueDate)}`}
                        style={{ position: 'absolute', left: `${duePct}%`, top: '50%', transform: 'translate(-50%, -50%) rotate(45deg)', width: 14, height: 14, background: barColor, cursor: 'pointer', zIndex: 3, boxShadow: '0 1px 4px rgba(0,0,0,0.2)', transition: 'transform 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translate(-50%, -50%) rotate(45deg) scale(1.4)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translate(-50%, -50%) rotate(45deg) scale(1)'; }} />
                    </div>
                  </div>
                );
              })}
              <div style={{ display: 'flex', borderTop: `1px solid ${B.border}`, background: B.surfaceMuted }}>
                <div style={{ width: 260, flexShrink: 0, borderRight: `1px solid ${B.border}` }} />
                <div style={{ flex: 1, position: 'relative', height: 24 }}>
                  {weeks.map((w, i) => <div key={i} style={{ position: 'absolute', left: `${w.pct}%`, top: 4, fontSize: 9, color: B.textLt, whiteSpace: 'nowrap', paddingLeft: 3 }}>{w.label}</div>)}
                </div>
              </div>
            </div>
          </Card>
        );
      })()}

      {selected && (
        <TaskDetailModal
          taskId={selected.taskID}
          users={users || []}
          targetNoteId={pendingNoteId}
          onNoteTargetHandled={() => setPendingNoteId(null)}
          onClose={() => { setSelected(null); setPendingNoteId(null); }}
          onChanged={reload}
        />
      )}

      {showModal && (
        <TaskCreateModal
          parentTask={addingChildFor}
          onClearParent={() => setAddingChildFor(null)}
          parentOptions={allTasks.filter(t => !t.parentTaskID).map(t => ({
            value: t.taskID, label: t.title, dealID: t.dealID, dealDetailID: t.dealDetailID,
          }))}
          users={users || []}
          contacts={contacts || []}
          onClose={() => { setShowModal(false); setAddingChildFor(null); }}
          onCreated={() => reload()}
        />
      )}

      {/* Quick-add's own near-duplicate confirm (the dialog lives with the
          create it belongs to — the New Task modal carries its own). */}
      {duplicateDialog}
      {showOverdueReport && <OverdueReportModal users={users || []} onClose={() => setShowOverdueReport(false)} />}
    </div>
  );
};
